import React, { useEffect, useState } from 'react';
import Barcode from 'react-barcode';
import styled from 'styled-components';
import { Text } from 'rebass/styled-components';
import { observer } from 'mobx-react';
import { useQuery } from '@apollo/client';

import useInstoreCart from 'hooks/use-instore-cart';
import {
  useDutchiePayAnalytics,
  INSTORE_EVENTS,
} from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';
import Bracket from 'assets/bracket';
import { GetEntityLinkingCodeDocument } from 'src/gql/graphql';
import { FullPageLoader } from 'shared/components';
import useViewport from 'hooks/use-viewport';

export function BarcodeViewComponent(): JSX.Element {
  const InstoreCart = useInstoreCart();
  const [showBarcode, setShowBarcode] = useState(true);
  const { associateTerm } = InstoreCart;
  const { dutchiePayEventTracker } = useDutchiePayAnalytics();

  const { data, loading } = useQuery(GetEntityLinkingCodeDocument, {
    skip: !InstoreCart.isValidCartUser,
  });

  const viewport = useViewport();

  const barcodeValue = data?.getEntityLinkingCode?.code;

  useEffect(() => {
    if (barcodeValue) {
      dutchiePayEventTracker(INSTORE_EVENTS.ACCOUNT_LINK_VIEWED, { showBarcode });
    }
  }, [barcodeValue, dutchiePayEventTracker, showBarcode]);

  if (loading) {
    return <FullPageLoader />;
  }

  return (
    <Container data-testid='barcode-view' height={viewport.height}>
      <InnerContainer>
        <Header>
          Show {showBarcode ? 'barcode' : 'code'} to <br /> your {associateTerm}
        </Header>
        {showBarcode ? (
          <BarcodeOuterContainer>
            <BracketContainer>
              <Bracket />
              <Bracket rotation={90} />
            </BracketContainer>
            <BarcodeContainer>
              <Barcode value={barcodeValue} displayValue={false} width={3} height={88} />
            </BarcodeContainer>
            <BracketContainer>
              <Bracket rotation={270} />
              <Bracket rotation={180} />
            </BracketContainer>
          </BarcodeOuterContainer>
        ) : (
          <Code>{barcodeValue}</Code>
        )}
      </InnerContainer>
      <InnerContainer>
        {showBarcode && <FooterText>Scanner not working?</FooterText>}
        <Button onClick={() => setShowBarcode(!showBarcode)}>
          {showBarcode ? 'Enter Code Manually' : 'Back to Barcode'}
        </Button>
      </InnerContainer>
    </Container>
  );
}

export const BarcodeView = observer(BarcodeViewComponent);

const offset = 112;

const Container = styled.div<{ height: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  min-height: ${(props) => props.height}px;
  margin-top: -${offset}px;
  width: 100%;
`;

const BarcodeOuterContainer = styled.div`
  background: ${({ theme }) => theme.colors.white};
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
  border-radius: 35px;
`;

const BarcodeContainer = styled.div`
  margin: -24px;
`;

const BracketContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 0 24px;
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  justify-content: space-around;
`;

const Button = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.white};
  text-decoration: underline;
  font-size: 16px;
  cursor: pointer;
  font-weight: 600;
`;

const Header = styled(Text)`
  color: ${({ theme }) => theme.colors.white};
  font-size: 32px;
  margin: calc(24px + ${offset}px) 0 44px 0;
  line-height: 38px;
  text-align: center;
  font-weight: 600;
`;

const FooterText = styled(Text)`
  color: ${({ theme }) => theme.colors.green[85]};
  font-size: 13px;
  margin-bottom: 8px;
  font-weight: 600;
`;

const Code = styled(Text)`
  font-size: 48px;
  letter-spacing: 21px;
  padding: 35px 0 35px 21px;
  font-weight: 700;
  border-radius: 35px;
  color: ${({ theme }) => theme.colors.grey[25]};
  background: ${({ theme }) => theme.colors.white};
  width: 100%;
  text-align: center;
`;
